import { useEffect } from 'react'
import * as React from 'react'
import { Route, Switch, withRouter, Redirect, BrowserRouter, useLocation } from 'react-router-dom'
import styled from 'styled-components'
import { tealiumTrackPageView, global as utilsGlobal } from '@navinc/utils'
import Bugsnag from '@bugsnag/js'
import { GlobalStyles } from './global-styles.js'

import BugsnagPluginReact from '@bugsnag/plugin-react'
import { ToastProvider, LoadingView, InfoDrawer, InfoDrawerProvider } from '@navinc/base-react-components'
import { AppThemeProvider } from './app-theme-provider.js'
import * as chunks from './chunks'
import { Premium } from './pages/premium'
import { Basic } from './pages/basic'
import { WithIsAuthorized } from './components/with-is-authorized'
import { AccountProvider } from './components/account-provider'
import { Terms as WithTerms } from './components/terms'
import { Layout } from './components/styled'
import { ScrollToTop } from './components/scroll-to-top'
import { SideBar } from './components/side-bar'
import { Footer } from './components/footer'
import { whenSidebarFits } from './theme'
import { ToastBar } from './components/toast-bar'
import { BillingDetailsProvider } from '../client/pages/settings/use-billing-details'
import { BillingStatusProvider } from '../client/pages/settings/use-billing-status'
import { usePartnerConfig, PartnerConfigProvider } from './hooks/use-partner-config'
import pkg from '../package.json'
import { usePartner } from './hooks/apollo/use-partner'
import { useCapabilities } from './hooks/apollo/use-capabilities'
import { useAccount } from './hooks/apollo/use-account'
import { LaunchDarklyProviderWithData as LaunchDarklyProvider } from './components/launch-darkly-provider.js'
import { CenteredLoadingComponent } from './components/async-component'
import { createGuardedRoute } from './utils/guarded-route.js'

const { CLIENT_NOTIFIER_API_KEY, CLIENT_NOTIFIER_RELEASE_STAGE } = global.process.env

const StyledInfoDrawer = styled(InfoDrawer)`
  z-index: 20;
`

/**
 * When the page first renders the route isn't there so the sidebar is in the
 * center this fixes it
 */
const RoutesWrapper = styled.div`
  width: 100%;
  min-height: 100vh;
  padding-top: 132px;
  position: relative;
  background-color: ${({ theme }) => theme.navSecondary};

  > :nth-last-child(2) {
    margin-bottom: 158px;
  }

  > :last-child {
    position: absolute;
    backface-visibility: hidden;
    bottom: 0;

    @media (${whenSidebarFits}) {
      margin: 0 16px;
    }
  }
  @supports (position: sticky) {
    padding: 0;
  }

  @media (${whenSidebarFits}) {
    padding-top: 0;
    padding-left: 330px;

    @supports (position: sticky) {
      padding: 0;
    }
  }
`

const StyledToastBar = styled(ToastBar)`
  z-index: 20;
  position: fixed;
`

const RoutesProviders = ({ children }) => {
  Bugsnag.start({
    apiKey: CLIENT_NOTIFIER_API_KEY,
    releaseStage: CLIENT_NOTIFIER_RELEASE_STAGE,
    enabledReleaseStages: ['production', 'sandbox', 'staging', 'integration'],
    appVersion: pkg.version,
    plugins: [new BugsnagPluginReact(React)],
  })

  const ErrorBoundary = Bugsnag.getPlugin('react')
  const { pathname } = useLocation()

  // temporary: will be removed - this is for ease of testing re-branding changes locally

  return (
    <LaunchDarklyProvider>
      <AppThemeProvider>
        <GlobalStyles />
        <ToastProvider>
          <ErrorBoundary>
            <Layout>
              <InfoDrawerProvider>
                <StyledToastBar />
                <StyledInfoDrawer />
                <PartnerConfigProvider>
                  <BrowserRouter>
                    <Route path="/partner/paycheck-protection-program-form" component={chunks.LoggedOutPPPFormPage} />
                  </BrowserRouter>
                  {/* This hack is temporary. This gives us a way to add routes that are viewable outside authentication.
                We need this until we move the SBA PPP Form into its own application */}
                  {!pathname.includes('/partner/paycheck-protection-program-form') && (
                    <WithIsAuthorized>
                      <AccountProvider>
                        <BillingStatusProvider>
                          <BillingDetailsProvider>
                            <WithTerms>{children}</WithTerms>
                          </BillingDetailsProvider>
                        </BillingStatusProvider>
                      </AccountProvider>
                    </WithIsAuthorized>
                  )}
                </PartnerConfigProvider>
              </InfoDrawerProvider>
            </Layout>
          </ErrorBoundary>
        </ToastProvider>
      </AppThemeProvider>
    </LaunchDarklyProvider>
  )
}

const RoutesSwitch = () => {
  const {
    isPremiumAccount,
    isCreditReportsEnabled,
    isMarketPlaceLoansEnabled,
    loading: isCapabilitiesLoading,
  } = useCapabilities()

  const { loading: isPartnerLoading } = usePartner()

  const {
    configurations: { usesModularizedOnboardingFlow, hasSideBarEnabled, isUserSettingsEnabled },
    isLoading: isPartnerConfigLoading,
    partnerConfig,
  } = usePartnerConfig()

  const { getAccountId, getBusinessBin, getIsOptedIn, getFirstBusiness, loading: isAccountLoading } = useAccount()
  const { interestedInFinancing } = getFirstBusiness()
  const isPageLoading = isCapabilitiesLoading || isPartnerConfigLoading || isAccountLoading

  const missingBin = !getBusinessBin()
  const isOptedIn = getIsOptedIn()
  const accountId = getAccountId()

  const isInterestedInFinancingAnswered = interestedInFinancing !== null
  const shouldVdfBlockMarketplace = !isInterestedInFinancingAnswered || !isOptedIn

  const SettingsRoute = createGuardedRoute({
    condition: isUserSettingsEnabled,
    isConditionLoading: isPartnerLoading,
    LoadingComponent: LoadingView,
    FailureComponent: chunks.NotFoundPage,
  })
  const { pathname } = useLocation()
  useEffect(() => {
    tealiumTrackPageView({
      tealium_event: 'pageView',
      path: pathname,
      url: utilsGlobal.window.location.href,
      accountId,
      client: 'enterprise',
      pageName: utilsGlobal.document.title,
    })
    // eslint-disable-next-line
  }, [pathname, accountId])

  return (
    <>
      {hasSideBarEnabled && <SideBar />}
      <RoutesWrapper data-testid="ent:routes-wrapper">
        {isPageLoading ? (
          <CenteredLoadingComponent />
        ) : (
          <ScrollToTop>
            <Switch>
              <Redirect exact from="/" to="/partner/home" />
              <Redirect exact from="/partner" to="/partner/home" />
              <Redirect from="/partner/reports/basic/xpn" to="/partner/reports/xpn/overview" />
              <Redirect from="/partner/reports/score/xpn" to="/partner/reports/xpn/overview" />
              <Redirect from="/partner/reports/alerts/xpn" to="/partner/reports/xpn/alerts" />
              <Route path="/partner/home" component={chunks.HomePage} />

              <Route path="/embedded" component={chunks.EmbeddedPage} />
              <Route path="/partner/contact" component={chunks.ContactPage} />
              <Route path="/partner/education/tradeline" component={chunks.TradelinePage} />

              {/* These 4 redirects determine which flow a partner should see.Clover uses the old idf flow. Creditsafe and future partners use MOF */}
              {shouldVdfBlockMarketplace && !usesModularizedOnboardingFlow && (
                <Redirect
                  from="/partner/financing/credit-cards"
                  to={{ pathname: '/partner/financing/opt-in', state: { referrer: '/partner/financing/credit-cards' } }}
                />
              )}

              {!isMarketPlaceLoansEnabled && <Redirect from="/partner/financing/loans" to="/partner/home" />}
              {shouldVdfBlockMarketplace && !usesModularizedOnboardingFlow && (
                <Redirect from="/partner/financing/loans" to="/partner/financing/opt-in" />
              )}
              {shouldVdfBlockMarketplace && usesModularizedOnboardingFlow && (
                <Redirect
                  from="/partner/financing/credit-cards"
                  to={{
                    pathname: '/partner/financing/opt-in-mof',
                    state: { referrer: '/partner/financing/credit-cards' },
                  }}
                />
              )}
              {shouldVdfBlockMarketplace && usesModularizedOnboardingFlow && (
                <Redirect
                  from="/partner/financing/loans"
                  to={{
                    pathname: '/partner/financing/opt-in-mof',
                    state: { referrer: '/partner/financing/loans' },
                  }}
                />
              )}

              <Route path="/partner/financing/credit-cards/:id([0-9]+)" component={chunks.CCDetailPage} />
              <Route path="/partner/financing/credit-cards" component={chunks.CCListingPage} />
              <Route
                path="/partner/financing/loans/:typeSlug([a-z_-]+)/:id([0-9]+)"
                component={chunks.LoanDetailPage}
              />
              <Route path="/partner/financing/loans/:typeSlug([a-z_-]+)" component={chunks.LoanTypeListingPage} />
              <Route path="/partner/financing/loans" component={chunks.LoanTypesPage} />
              <Route path="/partner/financing/opt-in" component={chunks.OptInPage} />
              <Route path="/partner/financing/opt-in-mof" component={chunks.OptInMOFPage} />
              <Route path="/partner/financing/best-phone-number" component={chunks.BestPhoneNumber} />
              <Route path="/partner/profile" component={chunks.BusinessProfilePage} />
              <Route path="/partner/funding-application" component={chunks.FundingApplication} />

              <Route
                path="/partner/business-match/:bureauId([a-z]+)/results"
                component={chunks.BusinessMatchResultsPage}
              />
              <Route
                path="/partner/business-match/:bureauId([a-z]+)/no-match"
                component={chunks.BusinessMatchNoMatchPage}
              />
              <Route path="/partner/business-match/:bureauId([a-z]+)" component={chunks.BusinessMatchSearchPage} />

              {missingBin && <Redirect from="/partner/reports/xpn" to="/partner/business-match/xpn" />}

              {isCreditReportsEnabled && (
                <Route path="/partner/reports" component={isPremiumAccount ? Premium : Basic} />
              )}
              <SettingsRoute path="/partner/settings/authorizations" component={chunks.SettingsAuthorizationsPage} />
              <SettingsRoute path="/partner/settings/account-details" component={chunks.SettingsAccountDetailsPage} />
              <SettingsRoute path="/partner/settings/billing-info" component={chunks.SettingsBillingInfoPage} />
              <SettingsRoute path="/partner/settings/business" component={chunks.SettingsBusinessPage} />
              <SettingsRoute path="/partner/settings/security" component={chunks.SecurityPage} />
              <SettingsRoute path="/partner/settings/plan-details" component={chunks.SettingsPlanDetailsPage} />
              <SettingsRoute path="/partner/settings/cancel" component={chunks.SettingsCancelPlanPage} />
              <SettingsRoute path="/partner/settings/update-billing" component={chunks.SettingsUpdateBillingPage} />
              <SettingsRoute path="/partner/settings" exact component={chunks.SettingsPage} />

              <Route
                path="/partner/upgrade-needed"
                render={() => (
                  <chunks.NotFoundPage
                    message={
                      <span>
                        Your account is currently inactive. Work with {partnerConfig.name} to reactivate your account.
                        {partnerConfig.customerSupportEmail && (
                          <>
                            <br />
                            <br />
                            <a href={`mailto:${partnerConfig.customerSupportEmail}`}>
                              {partnerConfig.customerSupportEmail}
                            </a>
                          </>
                        )}
                        {partnerConfig.customerSupportPhone && (
                          <>
                            <br />
                            <a href={`tel:${partnerConfig.customerSupportPhone}`}>
                              {partnerConfig.customerSupportPhone}
                            </a>
                          </>
                        )}
                      </span>
                    }
                    statusCode="403"
                  />
                )}
              />
              <Route component={chunks.NotFoundPage} />
            </Switch>
          </ScrollToTop>
        )}
        <Footer />
      </RoutesWrapper>
    </>
  )
}

export const Routes = withRouter(() => (
  <RoutesProviders>
    <RoutesSwitch />
  </RoutesProviders>
))
